import { isLoggedInQuerySelector } from 'auth/selectors'
import { useSiteDataQuery } from 'codegen/generated/cf'
import { useUserBlockslistedQuery } from 'codegen/generated/user'
import { useUserQuery } from 'auth/hooks'

import { keepPreviousData } from '@tanstack/react-query'

// oxlint-disable-next-line complexity
export const useUserPromoPopupData = () => {
  const { data, isPending: isLoading } = useUserQuery(undefined, {
    // oxlint-disable-next-line complexity
    select: data => ({
      isTrial: data?.me?.user?.subscriptionAccess.allAccess.subscription?.isTrial ?? false,
      isLoggedIn: isLoggedInQuerySelector(data),
      isAllAccess: data?.me?.user?.subscriptionAccess.allAccess.isSubscribed ?? false,
      isEligibleForAllAccessYearlyUpgrade:
        data?.me?.user?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade ?? false,
      stripeCustomerId: data?.me?.user?.stripeCustomerId ?? null,
      isEligibleForAllAccessFreeTrial:
        data.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial ?? null,
    }),
    placeholderData: keepPreviousData,
  })
  const { data: isUserBlocklisted, isPending: isUserBlocklistedLoading } = useUserBlockslistedQuery(
    undefined,
    {
      select: data => data?.me?.user?.isUserBlocklisted.extensionPopup ?? false,
      enabled: !isLoading && data?.isLoggedIn === true,
    },
  )
  const {
    isLoggedIn,
    isAllAccess,
    isEligibleForAllAccessYearlyUpgrade,
    isEligibleForAllAccessFreeTrial,
    stripeCustomerId,
  } = data ?? {
    isLoggedIn: false,
    isAllAccess: false,
    isEligibleForAllAccessYearlyUpgrade: false,
    isEligibleForAllAccessFreeTrial: false,
    stripeCustomerId: null,
  }
  const { data: siteData, isPending: siteDataLoading } = useSiteDataQuery(undefined, {
    select: data => ({
      banner: data?.siteData?.undoCancelationBanner,
      isExtensionPopupEnabled: data?.siteData?.isExtensionPopupEnabled?.enabled,
    }),
    enabled: !isLoading && isLoggedIn,
  })
  const isYearlyPopupBlocklistLoading =
    !siteDataLoading &&
    isLoggedIn &&
    isAllAccess &&
    !isEligibleForAllAccessFreeTrial &&
    !isEligibleForAllAccessYearlyUpgrade &&
    siteData?.isExtensionPopupEnabled &&
    isUserBlocklistedLoading

  if (isLoading || (isLoggedIn && siteDataLoading) || isYearlyPopupBlocklistLoading) {
    return {
      isPending: true,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  if (!isLoggedIn) {
    return {
      isPending: false,
      loggedOutPromo: true,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  if (isLoggedIn && !isAllAccess && !siteDataLoading) {
    return {
      isPending: false,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: true,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  if (isLoggedIn && !siteDataLoading && siteData?.banner) {
    return {
      isPending: false,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: true,
      stripeCustomerId,
    }
  }

  if (isLoggedIn && isAllAccess && data?.isTrial) {
    return {
      isPending: false,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: true,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  if (isLoggedIn && isAllAccess && isEligibleForAllAccessYearlyUpgrade && !siteDataLoading) {
    return {
      isPending: false,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: true,
      loggedInYearlyPromo: false,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  if (
    !siteDataLoading &&
    isLoggedIn &&
    isAllAccess &&
    !isEligibleForAllAccessFreeTrial &&
    !isEligibleForAllAccessYearlyUpgrade &&
    siteData?.isExtensionPopupEnabled &&
    !isUserBlocklistedLoading &&
    !isUserBlocklisted
  ) {
    return {
      isPending: false,
      loggedOutPromo: false,
      loggedInUnsubscribedPromo: false,
      loggedInTrialPromo: false,
      loggedInMonthlyPromo: false,
      loggedInYearlyPromo: true,
      loggedInCancelledPromo: false,
      stripeCustomerId,
    }
  }

  return {
    isPending: false,
    loggedOutPromo: false,
    loggedInUnsubscribedPromo: false,
    loggedInTrialPromo: false,
    loggedInMonthlyPromo: false,
    loggedInYearlyPromo: false,
    loggedInCancelledPromo: false,
    stripeCustomerId,
  }
}
